import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import {
  Avatar, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Stack, Typography
} from '@mui/material';
import { Close as CloseIcon, PersonRemove as RevokeIcon, WarningAmberRounded as WarningIcon } from '@mui/icons-material';
import ConfirmDeleteModal from 'components/shared/modals/ConfirmDeleteModal';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';
import {
  MY_PROGRESS_SHARES, SHAREABLE_TRAINERS, SHARE_PHYSICAL_PROGRESS, REVOKE_PHYSICAL_PROGRESS_SHARE
} from 'api/graphql/physicalProgress';

/**
 * Modal para que el cliente comparta su progreso físico con entrenadores activos (o revoque).
 */
function ProgressShareModal({ open, onClose }) {
  const dispatch = useDispatch();
  const { data: trainersData, loading: loadingTrainers } = useQuery(SHAREABLE_TRAINERS, { fetchPolicy: 'cache-and-network', skip: !open });
  const { data: sharesData, refetch } = useQuery(MY_PROGRESS_SHARES, { fetchPolicy: 'cache-and-network', skip: !open });
  const [share, { loading: sharing }] = useMutation(SHARE_PHYSICAL_PROGRESS);
  const [revoke] = useMutation(REVOKE_PHYSICAL_PROGRESS_SHARE);

  const [selected, setSelected] = useState([]);
  const [confirmShare, setConfirmShare] = useState(false);
  const [toRevoke, setToRevoke] = useState(null);

  const trainers = trainersData?.shareableTrainers ?? [];
  const shares = sharesData?.myProgressShares ?? [];
  const sharedIds = useMemo(() => new Set(shares.map((s) => String(s.trainer.id))), [shares]);

  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const doShare = async () => {
    try {
      await share({ variables: { trainerIds: selected } });
      dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `share-${Date.now()}`, variant: 'success', message: 'Acceso concedido' })));
      setSelected([]);
      setConfirmShare(false);
      refetch();
    } catch { /* errorHandler */ }
  };

  const doRevoke = async () => {
    if (!toRevoke) return;
    await revoke({ variables: { trainerId: toRevoke.id } });
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `revoke-${Date.now()}`, variant: 'success', message: 'Acceso retirado' })));
    setToRevoke(null);
    refetch();
  };

  const candidates = trainers.filter((tr) => !sharedIds.has(String(tr.id)));

  const row = (tr, action) => (
    <Stack key={tr.id} direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
      <Avatar src={tr.photo} alt={tr.full_name} sx={{ width: 36, height: 36 }}>{tr.full_name?.[0]}</Avatar>
      <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 600 }} noWrap>{tr.full_name}</Typography>
      {action}
    </Stack>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { backgroundColor: 'surface.content', backgroundImage: 'none', border: '1px solid', borderColor: 'hairline.main' } }}>
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Compartir mi progreso
          <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {shares.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>Compartido con</Typography>
              {shares.map((s) => row(s.trainer, (
                <IconButton size="small" onClick={() => setToRevoke(s.trainer)} sx={{ color: 'secondary.main' }} aria-label="quitar acceso"><RevokeIcon fontSize="small" /></IconButton>
              )))}
              <Divider sx={{ borderColor: 'hairline.main', mt: 1.5 }} />
            </Box>
          )}

          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 0.5 }}>Entrenadores</Typography>
          {(() => {
            if (loadingTrainers && candidates.length === 0) {
              return <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={22} sx={{ color: 'secondary.main' }} /></Box>;
            }
            if (candidates.length === 0) {
              return <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>No hay más entrenadores para compartir.</Typography>;
            }
            return candidates.map((tr) => row(tr, (
              <Checkbox checked={selected.includes(tr.id)} onChange={() => toggle(tr.id)} sx={{ color: 'text.secondary', '&.Mui-checked': { color: 'secondary.main' } }} />
            )));
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button color="inherit" onClick={onClose} sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 700 }}>Cerrar</Button>
          <Button variant="contained" color="primary" disabled={selected.length === 0 || sharing} onClick={() => setConfirmShare(true)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Compartir ({selected.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación tipo WARNING (no el modal destructivo de eliminar): compartir fotos es sensible. */}
      <Dialog open={confirmShare} onClose={() => setConfirmShare(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { backgroundColor: 'surface.content', backgroundImage: 'none', border: '1px solid', borderColor: 'gold.main' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 800 }}>
          <WarningIcon sx={{ color: 'gold.main' }} />
          Confirmar acceso
        </DialogTitle>
        <DialogContent>
          <Box sx={{ backgroundColor: 'gold.soft', border: '1px solid', borderColor: 'gold.main', borderRadius: '12px', p: 1.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Vas a dar acceso a tu progreso, fotos incluidas, a {selected.length} entrenador(es).
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
              Podrán ver tus fotos y medidas. Puedes revocar el acceso cuando quieras.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button color="inherit" onClick={() => setConfirmShare(false)} sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 700 }}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={doShare} disabled={sharing} sx={{ textTransform: 'none', fontWeight: 700 }}>Sí, compartir</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDeleteModal
        open={toRevoke != null}
        onClose={() => setToRevoke(null)}
        onConfirm={doRevoke}
        title="Quitar acceso"
        message={toRevoke ? `¿Quitar el acceso de ${toRevoke.full_name} a tu progreso?` : ''}
        count={1}
      />
    </>
  );
}

ProgressShareModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ProgressShareModal;
